import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { getEmployeeSalaryHistory } from "../../../../services/payrollService";
import { currentSalary, employeeIdOf, idOf } from "../utils/payrollHelpers";

const emptyForm = {
  effectiveFrom: "",
  policyId: "",
  basic: "",
  hra: "",
  conveyance: "",
  other: "",
  paymentMode: "BANK",
  overtimeEligible: false,
  overtimeRate: "",
  remarks: "",
};

const SalaryStructureModal = ({
  open,
  employee,
  policies = [],
  loading = false,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] =
    useState(emptyForm);

  const [structure, setStructure] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const employeeId = employeeIdOf(employee);
  const structureId = idOf(employee?.salaryStructureId);
  const defaultPolicyId = policies.find(item => item.isDefault)?._id || policies[0]?._id || "";
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setFetching(true); setLoadError(""); setError(""); setStructure(null);
    setForm({ ...emptyForm, policyId: defaultPolicyId });
    async function load() {
      try {
        if (!employeeId) throw new Error("This payroll record is missing its employee reference.");
        const result = await getEmployeeSalaryHistory(employeeId);
        const history = Array.isArray(result) ? result : result?.items || result?.records || [];
        const current = structureId ? history.find(item => idOf(item) === structureId) : currentSalary(history);
        if (structureId && !current) throw new Error("The salary structure used for this payroll could not be found. Refresh the register and try again.");
        if (cancelled) return;
        setStructure(current || null);
        const amount = code => current?.earnings?.find(item => item.code === code)?.amount ?? 0;
        setForm({ ...emptyForm,
          policyId: idOf(current?.policyId) || defaultPolicyId,
          effectiveFrom: current?.effectiveFrom?.slice(0, 10) || new Date().toISOString().slice(0, 10),
          basic: current ? String(amount("BASIC")) : "", hra: String(amount("HRA")),
          conveyance: String(amount("CONVEYANCE")), other: String(amount("OTHER")),
          paymentMode: current?.paymentMode || "BANK", remarks: current?.remarks || "",
          overtimeEligible: Boolean(current?.overtime?.eligible), overtimeRate: String(current?.overtime?.hourlyRate ?? 0),
        });
      } catch (failure) { if (!cancelled) setLoadError(failure.message || "Could not load the salary structure."); }
      finally { if (!cancelled) setFetching(false); }
    }
    load();
    return () => { cancelled = true; };
  }, [open, employeeId, structureId, defaultPolicyId, retry]);

  const extraEarnings = useMemo(() => (structure?.earnings || []).filter(item => !["BASIC", "HRA", "CONVEYANCE", "OTHER"].includes(item.code)), [structure]);

  const change = (
    field,
    value
  ) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const gross = useMemo(
    () =>
      ["basic", "hra", "conveyance", "other"]
        .reduce(
          (total, field) =>
            total +
            Number(form[field] || 0),
          extraEarnings.reduce((sum, item) => sum + Number(item.amount || 0), 0)
        ),
    [form, extraEarnings]
  );

  if (!open) return null;

  const submit = async (event) => {
    event.preventDefault();

    const basic =
      Number(form.basic || 0);

    const hra =
      Number(form.hra || 0);

    const conveyance =
      Number(
        form.conveyance || 0
      );

    const other =
      Number(form.other || 0);

    setError("");
    try { await onSubmit?.({
      employeeId:
        employeeId,

      payload: {
        fixedDeductions: structure?.fixedDeductions || [],
        statutory: structure?.statutory || {},
        policyId:
          form.policyId,

        effectiveFrom:
          form.effectiveFrom,

        monthlyGross:
          gross,

        annualCTC:
          gross * 12,

        paymentMode:
          form.paymentMode,

        remarks:
          form.remarks,

        earnings: [...extraEarnings, ...[
          ["BASIC", "Basic Salary", basic], ["HRA", "House Rent Allowance", hra],
          ["CONVEYANCE", "Conveyance Allowance", conveyance], ["OTHER", "Other Allowance", other],
        ].map(([code, name, amount]) => ({
          code, name, taxable: true, proratable: true,
          ...structure?.earnings?.find(item => item.code === code), amount,
        }))],

        overtime: {
          ...structure?.overtime,
          eligible:
            form.overtimeEligible,

          calculationType:
            structure?.overtime?.calculationType || "HOURLY_FIXED",

          hourlyRate:
            Number(
              form.overtimeRate || 0
            ),

          multiplier: structure?.overtime?.multiplier ?? 1,
        },
      },
    }); } catch (failure) { setError(failure.message || "Could not save the salary structure."); }
  };

  return (
    <div className="se-payroll-modal-backdrop">
      <div className="se-payroll-modal se-payroll-modal--large" role="dialog" aria-modal="true" aria-labelledby="salary-structure-title">
        <div className="se-payroll-modal__header">
          <div>
            <span className="se-payroll-eyebrow">
              SALARY STRUCTURE
            </span>

            <h2 id="salary-structure-title">
              {employee?.fullName ||
                "Employee"}
            </h2>

            <p>
              {employee?.employeeCode ||
                ""}
              {employee?.designation
                ? ` · ${employee.designation}`
                : ""}
            </p>
          </div>

          <button
            type="button"
            aria-label="Close salary structure"
            disabled={loading}
            className="se-payroll-modal__close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <form onSubmit={submit}>
          {fetching && <p className="se-payroll-form-note" role="status">Loading saved salary structure…</p>}
          {loadError && <div role="alert" className="se-payroll-form-alert"><strong>Salary structure couldn’t be loaded</strong><p>{loadError}</p><button type="button" onClick={() => setRetry(value => value + 1)}>Try again</button></div>}
          {error && <div role="alert" className="se-payroll-form-alert"><strong>Salary structure wasn’t saved</strong><p>{error}</p></div>}
          {!fetching && !loadError && <p className="se-payroll-form-note">{structure ? "Saved salary loaded. Changes create a draft revision; the active salary remains in place until the revision is activated." : "No active salary structure found. Enter the employee’s salary to create a draft."}</p>}
          <fieldset className="se-payroll-form-fields" disabled={fetching || Boolean(loadError) || loading}>
          <div className="se-payroll-form-grid">
            <label>
              <span>
                Effective From
              </span>

              <input
                type="date"
                required
                value={
                  form.effectiveFrom
                }
                onChange={(event) =>
                  change(
                    "effectiveFrom",
                    event.target.value
                  )
                }
              />
            </label>

            <label>
              <span>
                Payroll Policy
              </span>

              <select
                required
                value={
                  form.policyId
                }
                onChange={(event) =>
                  change(
                    "policyId",
                    event.target.value
                  )
                }
              >
                <option value="">
                  Select policy
                </option>

                {policies.map(
                  (policy) => (
                    <option
                      key={policy._id}
                      value={policy._id}
                    >
                      {policy.name}
                    </option>
                  )
                )}
              </select>
            </label>

            <label>
              <span>
                Basic Salary
              </span>

              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={form.basic}
                onChange={(event) =>
                  change(
                    "basic",
                    event.target.value
                  )
                }
              />
            </label>

            <label>
              <span>HRA</span>

              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={form.hra}
                onChange={(event) =>
                  change(
                    "hra",
                    event.target.value
                  )
                }
              />
            </label>

            <label>
              <span>
                Conveyance
              </span>

              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={
                  form.conveyance
                }
                onChange={(event) =>
                  change(
                    "conveyance",
                    event.target.value
                  )
                }
              />
            </label>

            <label>
              <span>
                Other Allowance
              </span>

              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={form.other}
                onChange={(event) =>
                  change(
                    "other",
                    event.target.value
                  )
                }
              />
            </label>
          </div>

          <div className="se-payroll-salary-total">
            <span>
              Gross Monthly Salary
            </span>

            <strong>
              ₹
              {gross.toLocaleString(
                "en-IN"
              )}
            </strong>

            <small>
              Annual: ₹
              {(gross * 12).toLocaleString(
                "en-IN"
              )}
            </small>
          </div>

          <div className="se-payroll-ot-config">
            <label className="se-payroll-checkbox">
              <input
                type="checkbox"
                checked={
                  form.overtimeEligible
                }
                onChange={(event) =>
                  change(
                    "overtimeEligible",
                    event.target.checked
                  )
                }
              />

              <span>
                Employee is eligible for
                overtime
              </span>
            </label>

            {form.overtimeEligible && (
              <label>
                <span>
                  OT Rate / Hour
                </span>

                <input
                  type="number"
                step="0.01"
                  min="0"
                  value={
                    form.overtimeRate
                  }
                  onChange={(event) =>
                    change(
                      "overtimeRate",
                      event.target.value
                    )
                  }
                />
              </label>
            )}
          </div>

          </fieldset>
          <div className="se-payroll-modal__footer">
            <button
              type="button"
              className="se-payroll-btn se-payroll-btn--secondary"
              onClick={onClose}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="se-payroll-btn se-payroll-btn--primary"
              disabled={loading || fetching || Boolean(loadError)}
            >
              {loading
                ? "Saving..."
                : structure ? "Save Draft Revision" : "Create Draft Structure"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SalaryStructureModal;